import { SupabaseClient } from '@supabase/supabase-js';
import { VideoData, Comment, CommentTrend, APIError } from '@/types/youtube';

export class DatabaseService {
  constructor(private supabase: SupabaseClient) {}

  /**
   * Store or update video data
   * @param video The video data to store
   */
  async upsertVideo(video: VideoData): Promise<void> {
    try {
      console.log('Inserting video:', {
        video_id: video.id,
        title: video.title
      });
      
      // First try using the regular table insert
      const { error } = await this.supabase.from('videos').upsert({
        video_id: video.id,
        title: video.title,
        description: video.description,
        published_at: video.publishedAt,
        thumbnail_url: video.thumbnailUrl,
        view_count: video.viewCount,
        like_count: video.likeCount,
        comment_count: video.commentCount
      }, {
        onConflict: 'video_id'
      });

      if (error) {
        console.error('Error with regular video insertion, trying RPC method:', error);
        
        // If regular insertion fails, try using the RPC function that bypasses RLS
        const { error: rpcError } = await this.supabase.rpc('insert_video_direct', {
          video_id_param: video.id,
          title_param: video.title,
          desc_param: video.description,
          published_at_param: video.publishedAt,
          thumbnail_url_param: video.thumbnailUrl,
          view_count_param: BigInt(video.viewCount),
          like_count_param: BigInt(video.likeCount),
          comment_count_param: BigInt(video.commentCount)
        });
        
        if (rpcError) {
          console.error('Error with RPC video insertion:', rpcError);
          throw rpcError;
        } else {
          console.log('Video inserted successfully via RPC function');
        }
      } else {
        console.log('Video inserted successfully via regular insert');
      }
      
      // Verify the video was inserted by fetching it directly
      const { data: checkData, error: checkError } = await this.supabase
        .from('videos')
        .select('video_id')
        .eq('video_id', video.id)
        .single();
        
      if (checkError) {
        console.error('Error verifying video insertion:', checkError);
        
        // Try to verify using a service function (bypassing RLS)
        const { data: rpcCheckData, error: rpcCheckError } = await this.supabase.rpc('check_video_exists', {
          target_video_id: video.id
        });
        
        if (rpcCheckError || !rpcCheckData) {
          console.error('Error verifying video via RPC:', rpcCheckError);
          throw new Error(`Video was not properly inserted: ${checkError.message}`);
        } else {
          console.log('Video verified via RPC: exists =', rpcCheckData);
        }
      } else if (!checkData) {
        console.error('Video was not found after insertion');
        throw new Error('Video was not properly inserted: verification failed');
      } else {
        console.log('Video insertion verified:', video.id);
      }
    } catch (error) {
      console.error('Video insertion failed:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Store comments in batches
   * @param comments Array of comments to store
   * @param batchSize Number of comments per batch
   */
  async storeComments(comments: Comment[], batchSize = 50): Promise<void> {
    try {
      console.log(`Storing ${comments.length} comments in batches of ${batchSize}`);
      
      for (let i = 0; i < comments.length; i += batchSize) {
        const batch = comments.slice(i, i + batchSize).map(comment => ({
          comment_id: comment.id,
          video_id: comment.videoId,
          author: comment.author,
          text: comment.text,
          like_count: comment.likeCount,
          published_at: comment.publishedAt
        }));

        const { error } = await this.supabase
          .from('comments')
          .upsert(batch, { onConflict: 'comment_id' });

        if (error) {
          console.error(`Error inserting comment batch ${i / batchSize + 1}:`, error);
          throw error;
        }
      }
      
      console.log('All comments stored successfully');
    } catch (error) {
      console.error('Comment storage failed:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Analyze comments for a specific video
   * @param videoId The video ID to analyze comments for
   */
  async analyzeComments(videoId: string): Promise<void> {
    try {
      console.log('Starting comment analysis for video:', videoId);
      
      const { error } = await this.supabase.rpc('analyze_video_comments', {
        target_video_id: videoId
      });

      if (error) {
        console.error('Error from analyze_video_comments RPC call:', error);
        throw error;
      }
      
      console.log('Comment analysis completed successfully for video:', videoId);
    } catch (error) {
      console.error('Comment analysis failed:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get comment trends for a video
   * @param videoId The video ID to get trends for
   */
  async getVideoTrends(videoId: string): Promise<CommentTrend[]> {
    try {
      const { data, error } = await this.supabase.rpc('get_video_trends', {
        target_video_id: videoId
      });

      if (error) throw error;
      return data.map(trend => ({
        keyword: trend.keyword,
        mentionCount: trend.mention_count,
        firstSeen: trend.first_seen,
        lastSeen: trend.last_seen
      }));
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Get all videos ordered by publish date
   */
  async getAllVideos(): Promise<VideoData[]> {
    try {
      console.log('Fetching all videos');
      
      // Check authentication status to help with debugging
      const { data: authData } = await this.supabase.auth.getSession();
      console.log('Auth status:', {
        isAuthenticated: !!authData?.session,
        role: authData?.session?.user?.role || 'none'
      });
      
      // Try direct query first as policies exist
      const { data, error } = await this.supabase
        .from('videos')
        .select('video_id, title, description, published_at, thumbnail_url, view_count, like_count, comment_count')
        .order('published_at', { ascending: false });

      if (error) {
        console.error('Error fetching videos directly:', error);
        
        // Fall back to RPC function that bypasses RLS
        console.log('Trying fallback method via RPC...');
        const { data: rpcData, error: rpcError } = await this.supabase.rpc('get_all_videos');
        
        if (rpcError) {
          console.error('Error using get_all_videos RPC:', rpcError);
          throw rpcError;
        }
        
        console.log(`Retrieved ${rpcData?.length || 0} videos via RPC function`);
        
        return (rpcData || []).map(video => ({
          id: video.video_id,
          title: video.title,
          description: video.description,
          publishedAt: video.published_at,
          thumbnailUrl: video.thumbnail_url,
          viewCount: Number(video.view_count),
          likeCount: Number(video.like_count),
          commentCount: Number(video.comment_count) || 0
        }));
      }
      
      console.log(`Retrieved ${data?.length || 0} videos via direct query`);
      
      return (data || []).map(video => ({
        id: video.video_id,
        title: video.title,
        description: video.description,
        publishedAt: video.published_at,
        thumbnailUrl: video.thumbnail_url,
        viewCount: video.view_count,
        likeCount: video.like_count,
        commentCount: video.comment_count || 0
      }));
    } catch (error) {
      console.error('Video retrieval failed:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Handle and format database errors
   */
  private handleError(error: unknown): APIError {
    if (error instanceof Error) {
      const apiError: APIError = error;
      apiError.code = 'DATABASE_ERROR';
      // Add more detailed error information if available
      if ('code' in (error as any)) {
        apiError.details = `Code: ${(error as any).code}`;
      }
      if ('details' in (error as any)) {
        apiError.details = `${apiError.details || ''} Details: ${(error as any).details}`;
      }
      return apiError;
    }
    return new Error('An unexpected database error occurred');
  }
}
